import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { deleteDocumentFile, deleteFolder, moveDocumentFile, moveFolder } from '../../api/documents';
import { folderContentsKeys } from '../../hooks';
import type { SelectableItem } from './useMultiselect';
import type { UseBulkOperationsOptions, UseBulkOperationsReturn, BulkOperationResult } from './useBulkOperations.types';


/**
 * Hook for managing bulk operations on documents and folders
 * 
 * Executes bulk move and delete operations in parallel using Promise.allSettled.
 * Items are optimistically removed before the API calls and reverted individually
 * if their call fails.
 * 
 * On partial failure a toast shows how many succeeded and how many failed.
 * On complete failure all optimistic removals are reverted.
 */
export function useBulkOperations({
  addOptimisticRemovals,
  revertOptimisticRemovals,
  clearSelection,
  onFilesDeleted,
}: UseBulkOperationsOptions): UseBulkOperationsReturn {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isBulkMoving, setIsBulkMoving] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  /**
   * Executes bulk move operation on selected items
   */
  const executeBulkMove = useCallback(
    async (items: SelectableItem[], targetFolderId: string | null) => {
      if (!items.length) return;

      const itemsToMove = filterItemsToMove(items, targetFolderId);
      if (!itemsToMove.length) {
        clearSelection();
        return;
      }

      const itemIds = itemsToMove.map((item) => item.id);
      addOptimisticRemovals(itemIds);
      setIsBulkMoving(true);

      try {
        const results = await Promise.allSettled(
          itemsToMove.map((item) => executeMoveOperation(item, targetFolderId))
        );

        const operationResult = processBulkOperationResults(results, itemIds);

        if (operationResult.failedCount > 0) {
          revertOptimisticRemovals(operationResult.failedIds);
        }

        showBulkMoveResult(operationResult, t);

        // Invalidate all folder listings so the target folder fetches fresh
        // data the next time it is opened.
        if (operationResult.succeededCount > 0) {
          void queryClient.invalidateQueries({ queryKey: folderContentsKeys.all });
        }

        clearSelection();
      } catch {
        revertOptimisticRemovals(itemIds);
        toast.error(t('documents.multiselect.bulkMoveError'));
      } finally {
        setIsBulkMoving(false);
      }
    },
    [addOptimisticRemovals, revertOptimisticRemovals, clearSelection, t, queryClient]
  );

  /**
   * Executes bulk delete operation on selected items
   */
  const executeBulkDelete = useCallback(
    async (items: SelectableItem[]) => {
      if (!items.length) return;

      const itemIds = items.map((item) => item.id);
      addOptimisticRemovals(itemIds);
      setIsBulkDeleting(true);

      try {
        const results = await Promise.allSettled(
          items.map((item) => executeDeleteOperation(item))
        );

        const operationResult = processBulkOperationResults(results, itemIds);

        if (operationResult.failedCount > 0) {
          revertOptimisticRemovals(operationResult.failedIds);
        }

        showBulkDeleteResult(operationResult, t);

        // Notify about successfully deleted files so the detail panel can close
        const deletedFileIds = getDeletedFileIds(operationResult.succeededIds, items);
        if (deletedFileIds.length > 0) {
          onFilesDeleted(deletedFileIds);
        }

        clearSelection();
      } catch {
        revertOptimisticRemovals(itemIds);
        toast.error(t('documents.multiselect.bulkDeleteError'));
      } finally {
        setIsBulkDeleting(false);
      }
    },
    [addOptimisticRemovals, revertOptimisticRemovals, clearSelection, onFilesDeleted, t]
  );

  return { isBulkMoving, isBulkDeleting, executeBulkMove, executeBulkDelete };
}


// Helper functions

/**
 * Filters out items that are already in the target folder
 */
const filterItemsToMove = (items: SelectableItem[], targetFolderId: string | null): SelectableItem[] => {
  return items.filter((item) => {
    const currentParent = 'parentId' in item ? item.parentId : item.folderId;
    return currentParent !== targetFolderId;
  });
};

/**
 * Processes bulk operation results and returns success/failure statistics
 */
const processBulkOperationResults = (results: PromiseSettledResult<void>[], itemIds: string[]): BulkOperationResult => {
  const failedIds = itemIds.filter((_, i) => results[i]?.status === 'rejected');
  const succeededIds = itemIds.filter((_, i) => results[i]?.status === 'fulfilled');

  return {
    succeededCount: succeededIds.length,
    failedCount: failedIds.length,
    failedIds,
    succeededIds,
  };
};

/**
 * Executes the appropriate move operation based on item type
 */
const executeMoveOperation = (item: SelectableItem, targetFolderId: string | null) => {
  const isFolder = 'parentId' in item;
  return isFolder
    ? moveFolder(item.id, { newParentId: targetFolderId })
    : moveDocumentFile(item.id, { newFolderId: targetFolderId });
};

/**
 * Executes the appropriate delete operation based on item type
 */
const executeDeleteOperation = (item: SelectableItem) => {
  const isFolder = item.type === "folder"
  return isFolder ? deleteFolder(item.id) : deleteDocumentFile(item.id);
};

/**
 * Filters out successfully deleted file IDs (not folders)
 */
const getDeletedFileIds = (succeededIds: string[], items: SelectableItem[]): string[] => {
  return succeededIds.filter((id) =>
    items.find((item) => item.id === id && item.type === "file")
  );
};

/**
 * Shows appropriate toast message based on operation result
 */
const showBulkMoveResult = (result: BulkOperationResult, t: (key: string, options?: any) => string) => {
  if (result.failedCount > 0) {
    toast.error(
      t('documents.multiselect.bulkMovePartialFailure', {
        succeeded: result.succeededCount,
        failed: result.failedCount,
      })
    );
  } else {
    toast.success(
      t('documents.multiselect.bulkMoveSuccess', { succeeded: result.succeededCount })
    );
  }
};

/**
 * Shows appropriate toast message for delete operations
 */
const showBulkDeleteResult = (result: BulkOperationResult, t: (key: string, options?: any) => string) => {
  if (result.failedCount > 0) {
    toast.error(
      t('documents.multiselect.bulkDeletePartialFailure', {
        succeeded: result.succeededCount,
        failed: result.failedCount,
      })
    );
  }
};
